import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createApiUrlWithPrefix } from '../config/api';
import { useAuth } from './AuthContext';

const LowStockContext = createContext();

export const useLowStock = () => {
  const context = useContext(LowStockContext);
  if (!context) {
    throw new Error('useLowStock must be used within a LowStockProvider');
  }
  return context;
};

export const LowStockProvider = ({ children }) => {
  const [hasLowStock, setHasLowStock] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const checkLowStock = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setHasLowStock(false);
        setLoading(false);
        return;
      }

      const response = await fetch(createApiUrlWithPrefix('inventory/all'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check if any item has low stock
        // Convert to numbers to ensure proper comparison (handles string values from API)
        const lowStockItems = data.some(item => {
          const currentQty = Number(item.currentQuantity) || 0;
          const minQty = Number(item.minimumQuantity) || 0;
          // Only flag as low stock if current quantity is less than or equal to minimum
          // and minimum quantity is greater than 0
          return currentQty <= minQty && minQty > 0;
        });
        
        setHasLowStock(lowStockItems);
      } else {
        setHasLowStock(false);
      }
    } catch (error) {
      console.error('Error checking low stock:', error);
      setHasLowStock(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check low stock when user logs in or on page refresh (user changes from null to object)
  useEffect(() => {
    if (user) {
      console.log('🔄 User logged in or page refreshed, checking low stock...');
      checkLowStock();
    } else {
      // User logged out, reset low stock status
      setHasLowStock(false);
      setLoading(false);
    }
  }, [user, checkLowStock]);

  // Check for low stock every 5 minutes when user is logged in
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      console.log('🔄 Periodic low stock check...');
      checkLowStock();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user, checkLowStock]);

  return (
    <LowStockContext.Provider value={{ hasLowStock, loading, checkLowStock }}>
      {children}
    </LowStockContext.Provider>
  );
};

